import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useShopShuffleMutation } from '@/api/queries/shop';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { playSound } from '@/common/lib/soundEffect';
import { type BoxList } from '@/common/types/shop/marketBoxes';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { shopBoxesImages } from '@/components/helpers/imagesImports';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H1 } from '@/components/Typography/Typography';
import { GreyBlurSmButton } from '@/components/ui/Button/Button';
import RadialCircleGradient from '@/components/ui/Gradient/RadialCircleGradient';
// import { VipIcon } from '@/components/ui/Icons';
import { Picture } from '@/components/ui/Picture/Picture';

import RewardList from './RewardList/RewardList';
import ChengeButton from './checkButtons';
import { infoBox } from './infoBox';

import css from './style.module.scss';

import arrows from '/img/shop/boxes/arrows.svg';
import circleLightEffectPng from '/img/shop/boxes/light.png';
import circleLightEffectWebp from '/img/shop/boxes/light.webp';
import shinePng from '/img/shop/boxes/shine.png';
import shineWebp from '/img/shop/boxes/shine.webp';

interface Props extends BoxList {
  cardType: 'small' | 'medium' | 'big';
}

const BoxCard: React.FC<Props> = ({
  name,
  tgem,
  reward,
  nextUseInSeconds,
  cardType,
}) => {
  const statusVIP = useCheckVIPstatus();
  const { openModal } = useContext(ModalContext);
  const boxSeconds = useCountDownTimer(
    nextUseInSeconds <= 0 ? 0 : nextUseInSeconds,
  );
  const [shuffle, { isLoading }] = useShopShuffleMutation();
  const { t } = useTranslation();

  useEffect(() => {
    const claimFreeBoxDate = localStorage.getItem('claim-free-box-date');

    if (name === 'free' && !claimFreeBoxDate && !!nextUseInSeconds) {
      localStorage.setItem('claim-free-box-date', new Date().toISOString());
    }
  }, [name, nextUseInSeconds]);

  const img = shopBoxesImages[name];
  const { title, text, gradient, add, ticketVariant } = infoBox[name];

  const bigProGoldPack: boolean =
    cardType === 'big' && ['pro', 'gold'].includes(name);

  const onBuyBox = (): void => {
    openModal({
      renderKind: 'buyMarketBox',
      props: { name, title, gradient, reward, tgem, boxSeconds, cardType },
    });
  };

  return (
    <div className="relative flex-1">
      {ticketVariant && (
        <div
          className={cn(
            'absolute top-[-8px] z-[4] whitespace-nowrap',
            ticketVariant.var === 'ticker-center' && !bigProGoldPack
              ? 'left-[50%] translate-x-[-50%]'
              : 'right-0',
          )}
        >
          <Ticker
            variant={bigProGoldPack ? 'blue' : ticketVariant.var}
            extraVariant={bigProGoldPack ? undefined : ticketVariant.extra}
            text={ticketVariant.text}
          />
        </div>
      )}
      <div className={cn(css.boxCard, css[cardType])}>
        {['small', 'medium'].includes(cardType) && (
          <RadialCircleGradient
            type={gradient}
            className={cn('top-[-55%] left-0', {
              'blur-sm opacity-30':
                gradient === 'yellow' && cardType === 'medium',
            })}
          />
        )}
        {['big'].includes(cardType) && (
          <RadialCircleGradient
            type={gradient}
            className="top-[-5%] left-[-5%] w-[150%] h-[150%]"
          />
        )}
        {['big'].includes(cardType) && (
          <div className="absolute top-0 left-0 z-[-2] h-full w-full">
            <picture>
              <source srcSet={circleLightEffectWebp} type="image/webp" />
              <img
                src={circleLightEffectPng}
                alt="shine"
                className="h-full w-full"
              />
            </picture>
          </div>
        )}
        <div className={cn(css.boxCardInfo)}>
          <div className="flex gap-2 items-center">
            <H1 weight="bold" className={css.title}>
              {t(title)}
            </H1>
            {/* {['big'].includes(cardType) && !bigProGoldPack && (
              <div className={css.dayVip}>
                <p>
                  <b>+1</b> day
                </p>
                <VipIcon width={24} height={24} />
              </div>
            )} */}
          </div>
          {['medium', 'big'].includes(cardType) && (
            <div
              className={cn(
                'flex items-center w-full flex-col gap-1',
                !!boxSeconds && 'opacity-[0.5]',
                cardType === 'medium' && 'order-3 relative z-[2]',
                cardType === 'big' && '!w-1/2',
              )}
            >
              <RewardList
                reward={reward}
                variant="small"
                packName={name}
                classNames={cn({
                  'justify-center [&>div]:backdrop-blur-sm':
                    cardType === 'medium',
                  'justify-start': cardType === 'big',
                })}
              />
            </div>
          )}
          <div
            className={cn(css.imageWrapper, {
              '!w-[15rem]': bigProGoldPack,
              'order-2 -mt-1 !size-24 -mb-6': cardType === 'medium',
            })}
          >
            <picture>
              <source srcSet={img.webp} type="image/webp" />
              <img src={img.png} alt={`${name} box`} />
            </picture>
            {['medium'].includes(cardType) && (
              <>
                <RadialCircleGradient
                  type={gradient}
                  className={cn('z-[-2] ', {
                    'top-[-100%] left-[-160%] w-[420%] h-[200%]':
                      gradient === 'blue',
                    'top-[-110%] left-[-100%] w-[300%] h-[200%] blur-[32px]':
                      gradient === 'yellow',
                  })}
                />
                <Picture
                  img={shinePng}
                  webp={shineWebp}
                  alt="shine"
                  className="z-[-1] h-48 top-[-4rem] mix-blend-overlay w-max left-1/2 -translate-x-1/2 absolute opacity-70"
                />
              </>
            )}
          </div>
          {text && <p className={css.text}>{t(text)}</p>}
          {add && (
            <div className="flex gap-[4px]">
              {add.map((el, index) => (
                <div key={index} className={css.additionItems}>
                  <img src={el} alt="" width={25} />
                  {[1, 2].includes(index) && (
                    <span
                      className={cn(
                        index === 1 ? 'text-[#F2FFE5]' : 'text-[#FFE8D3]',
                      )}
                    >
                      x1
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className={cn(css.buttons)}>
            {['medium', 'big'].includes(cardType) && (
              <GreyBlurSmButton
                className={cn('!w-full px-4', !!boxSeconds && 'opacity-[0.5]')}
                text={t('Shuffle')}
                isLoading={isLoading}
                classNamesText="gap-2 flex items-center"
                disabled={!!boxSeconds}
                onClick={() => {
                  playSound('buttons/shuffle');
                  shuffle({ type: name });
                }}
              >
                <img src={arrows} alt="arrows" />
              </GreyBlurSmButton>
            )}
            <ChengeButton
              name={name}
              boxSeconds={boxSeconds}
              disable={!!boxSeconds}
              t={t}
              onBuyBox={onBuyBox}
              statusVIP={statusVIP}
              tgem={tgem}
              className={cn(css.mnButton, '!h-8 mt-[5px]', {
                '!w-[90%]': bigProGoldPack,
                'xl:[&_span]:!text-sm': cardType === 'small',
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxCard;
